import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SearchBar from "@/components/feature/SearchBar";
import WorkCard from "@/components/base/WorkCard";
import NewsCard from "@/components/base/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { works } from "@/mocks/works";
import { news } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

type Tab = "all" | "works" | "news";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();
  const [tab, setTab] = useState<Tab>("all");

  useSeo({
    title: keyword ? `“${keyword}” 的搜索结果 — 天海翼影视内容站` : "站内搜索 — 天海翼影视内容站",
    description: keyword
      ? `在站内搜索“${keyword}”，查看相关的天海翼影视作品与明星资讯。`
      : "搜索天海翼的影视作品与相关资讯，支持按作品与资讯类型查看结果。",
    keywords: "天海翼搜索,影视作品搜索,资讯搜索,站内搜索",
  });

  const { matchedWorks, matchedNews } = useMemo(() => {
    const kw = keyword.toLowerCase();
    if (!kw) return { matchedWorks: [], matchedNews: [] };
    const w = works.filter((item) =>
      [item.title, item.category, item.summary, item.director, item.cast.join(" "), item.tags.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(kw),
    );
    const n = news.filter((item) =>
      [item.title, item.type, item.excerpt, item.content.join(" ")].join(" ").toLowerCase().includes(kw),
    );
    return { matchedWorks: w, matchedNews: n };
  }, [keyword]);

  const total = matchedWorks.length + matchedNews.length;

  const tabs: { key: Tab; label: string; count: number }[] = [
    { key: "all", label: "全部", count: total },
    { key: "works", label: "影视作品", count: matchedWorks.length },
    { key: "news", label: "明星资讯", count: matchedNews.length },
  ];

  const showWorks = tab === "all" || tab === "works";
  const showNews = tab === "all" || tab === "news";

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">站内搜索</h1>
          <p className="mt-3 text-sm text-foreground-600 md:text-base">
            {keyword ? (
              <>
                关于 <span className="font-medium text-primary-700">“{keyword}”</span> 的搜索结果，共{" "}
                <span className="font-medium text-primary-700">{total}</span> 条。
              </>
            ) : (
              "输入关键词，搜索天海翼的影视作品与相关资讯。"
            )}
          </p>
          <div className="mt-6 max-w-2xl">
            <SearchBar initialValue={keyword} autoFocus={!keyword} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        {!keyword ? (
          <EmptyState
            icon="ri-search-line"
            title="输入关键词开始搜索"
            description="你可以尝试搜索作品名称（如“春山旧梦”）、类型（如“悬疑”）或资讯关键词。"
          />
        ) : total === 0 ? (
          <EmptyState
            icon="ri-emotion-sad-line"
            title={`没有找到与“${keyword}”相关的内容`}
            description="换个关键词试试，或者直接浏览全部影视作品与最新资讯。"
          />
        ) : (
          <>
            <div className="mb-8 inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
              {tabs.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setTab(item.key)}
                  className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    tab === item.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-700 hover:text-primary-700"
                  }`}
                >
                  {item.label}
                  <span className={`ml-1.5 text-xs ${tab === item.key ? "text-background-50/80" : "text-foreground-500"}`}>
                    {item.count}
                  </span>
                </button>
              ))}
            </div>

            {showWorks && matchedWorks.length > 0 && (
              <div className="mb-14">
                <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-film-line text-primary-600" />
                  影视作品
                  <span className="text-sm font-normal text-foreground-500">（{matchedWorks.length}）</span>
                </h2>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 md:gap-5">
                  {matchedWorks.map((work, index) => (
                    <WorkCard key={work.id} work={work} index={index} />
                  ))}
                </div>
              </div>
            )}

            {showNews && matchedNews.length > 0 && (
              <div>
                <h2 className="mb-5 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-newspaper-line text-accent-500" />
                  明星资讯
                  <span className="text-sm font-normal text-foreground-500">（{matchedNews.length}）</span>
                </h2>
                <div className="flex flex-col gap-4">
                  {matchedNews.map((item, index) => (
                    <NewsCard key={item.id} item={item} variant="list" index={index} />
                  ))}
                </div>
              </div>
            )}

            {((!showWorks || matchedWorks.length === 0) && (!showNews || matchedNews.length === 0)) && (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="该分类下暂无可展示的结果"
                description="切换到“全部”标签查看其他相关内容。"
                actionText="查看全部结果"
                onAction={() => setTab("all")}
              />
            )}
          </>
        )}

        {keyword && total === 0 && (
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/works"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              浏览全部作品
            </Link>
            <Link
              to="/news"
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              浏览最新资讯
            </Link>
          </div>
        )}
      </section>
    </>
  );
}